// src/components/TaskCard.js
import React from 'react';
import styled from 'styled-components';

const Card = styled.div`
  background: ${({ theme }) => theme.card2 || '#fff'};
  border: 1px solid ${({ theme }) => theme.border || '#e0e0e0'};
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary || '#1a73e8'};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  ${({ $status, theme }) => $status === 'failed' && `
    border-color: ${theme.error || '#ea4335'};
    background: ${theme.error ? theme.error + '10' : '#fef7f7'};
  `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const Icon = styled.span`
  font-size: 18px;
  min-width: 20px;
  text-align: center;
  color: ${({ $status, theme }) => {
    switch($status) {
      case 'running': return theme.primary || '#1a73e8';
      case 'completed': return theme.success || '#34a853';
      case 'failed': return theme.error || '#ea4335';
      default: return theme.text || '#000';
    }
  }};
  
  ${({ $status }) => $status === 'running' && `
    animation: spin 1s linear infinite;
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `}
`;

const Title = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text || '#202124'};
  flex: 1;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${({ theme }) => theme.border2 || '#e0e0e0'};
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ width }) => width}%;
  background: ${({ theme }) => theme.primary || '#1a73e8'};
  transition: width 0.3s ease;
`;

const Message = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.text || '#5f6368'};
  opacity: 0.8;
  margin-bottom: 4px;
`;

const Time = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text || '#80868b'};
  opacity: 0.6;
`;

const TaskCard = ({ task }) => {
  const getIcon = () => {
    switch (task.status) {
      case 'running': return '⟳';
      case 'completed': return '✓';
      case 'failed': return '✕';
      default: return '○';
    }
  };

  const getTitle = () => {
    const action = task.type === 'archive_index' ? 'Archiving' : 'Restoring';
    const statusText = task.status === 'completed' 
      ? (task.type === 'archive_index' ? 'Archived' : 'Restored')
      : task.status === 'failed'
      ? (task.type === 'archive_index' ? 'Archive failed' : 'Restore failed')
      : action;
    
    return `${statusText}: ${task.indexName}`;
  };

  const getTimeAgo = (isoDate) => {
    const seconds = Math.floor((new Date() - new Date(isoDate)) / 1000);
    if (seconds < 60) return `${seconds} seconds ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  };

  return (
    <Card $status={task.status}>
      <Header>
        <Icon $status={task.status}>{getIcon()}</Icon>
        <Title>{getTitle()}</Title>
      </Header>
      
      {task.status === 'running' && (
        <ProgressBar>
          <ProgressFill width={task.progress} />
        </ProgressBar>
      )}
      
      <Message>{task.message}</Message>
      
      <Time>
        {task.status === 'completed' && task.completedAt
          ? `Completed ${getTimeAgo(task.completedAt)}`
          : `Started ${getTimeAgo(task.startedAt)}`
        }
      </Time>
    </Card>
  );
};

export default TaskCard;
